<script setup lang="ts">
import SideLoadMessage from "@/components/SideLoadMessage.vue";
import ApiComboBox from "@/components/task-pane/ApiComboBox.vue";
import ApiFunctionComboBox from "@/components/task-pane/ApiFunctionComboBox.vue";
import EnableAutoGenerateFormSwitch from "@/components/task-pane/EnableAutoGenerateFormSwitch.vue";
import InfoCard from "@/components/task-pane/InfoCard.vue";
import TaskPaneLoadingSkeleton from "@/components/task-pane/TaskPaneLoadingSkeleton.vue";
import SpeckleTransferSection from "@/components/task-pane/api-forms-panel/components/SpeckleTransferSection.vue";
import { useApiStore } from "@/stores/api/useApiStore";
import { useRuntimeStore } from "@/stores/runtime/useRuntimeStore";
import { HaskoningButton, HaskoningText } from "@haskoning/vue-component-library";
import { storeToRefs } from "pinia";
import { computed, onMounted } from "vue";

type RuntimeViewState = "outsideExcel" | "loading" | "error" | "ready";

const apiStore = useApiStore();
const { autoGenerateForm } = storeToRefs(apiStore);
const runtimeStore = useRuntimeStore();
const { officeState, authState, bootstrapState, runtimeError } = storeToRefs(runtimeStore);

const isReady = computed(
  () => officeState.value === "ready" && authState.value === "authenticated" && bootstrapState.value === "ready"
);

const runtimeViewState = computed<RuntimeViewState>(() => {
  if (officeState.value === "not-in-excel") {
    return "outsideExcel";
  }

  if (bootstrapState.value === "error") {
    return "error";
  }

  if (isReady.value) {
    return "ready";
  }

  return "loading";
});

async function loadRuntimeDataWhenReady(): Promise<void> {
  if (isReady.value) {
    await apiStore.initializeApiCatalog();
  }
}

async function handleRetry(): Promise<void> {
  await runtimeStore.retry();
  await loadRuntimeDataWhenReady();
}

onMounted(async () => {
  await runtimeStore.initializeRuntime();
  await loadRuntimeDataWhenReady();
});
</script>

<template>
  <div class="d-flex flex-column flex-1-1 ga-3 py-3 task-pane-view" style="min-height: 100%; padding-inline: 10px">
    <SideLoadMessage v-if="runtimeViewState === 'outsideExcel'" />

    <template v-else>
      <TaskPaneLoadingSkeleton v-if="runtimeViewState === 'loading'" />
      <div
        v-else-if="runtimeViewState === 'error'"
        class="d-flex flex-column align-center justify-center fill-height pa-4"
      >
        <v-icon color="error" size="48">mdi-alert-circle-outline</v-icon>
        <HaskoningText variant="body-medium" as="p" class="mt-2 text-center">
          {{ runtimeError?.message ?? "Runtime initialization failed." }}
        </HaskoningText>
        <HaskoningButton v-if="runtimeError?.retriable" color="primary" size="small" class="mt-4" @click="handleRetry">
          Retry
        </HaskoningButton>
      </div>

      <template v-else>
        <EnableAutoGenerateFormSwitch />
        <ApiComboBox />
        <ApiFunctionComboBox v-if="autoGenerateForm" />
        <SpeckleTransferSection />
        <InfoCard class="task-pane-view__card d-flex flex-column flex-1-1" />
      </template>
    </template>
  </div>
</template>

<style scoped>
.task-pane-view {
  background: rgb(var(--v-theme-onPrimary));
}

.task-pane-view__card :deep(.v-card-text) {
  flex: 1 1 auto;
}
</style>
